"use client";

import { useEffect, useState } from "react";

import { authClient, useSession } from "@/lib/auth-client";

export function ConversionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, isPending } = useSession();
  const [_authEventTriggered, setAuthEventTriggered] = useState(0); // Force re-render counter
  const [manualSessionData, setManualSessionData] = useState<unknown>(null); // Manual session override

  // Use manual session if available (after auth event), otherwise use hook session
  const isSignedIn = Boolean(manualSessionData || session);
  const isLoaded = !isPending;

  // Sync manual session with hook session when it updates
  useEffect(() => {
    if (session && !manualSessionData) {
      console.log(
        "🔄 CONVERSION PROVIDER: Syncing hook session to manual session",
      );
      setManualSessionData(session);
    }
  }, [session, manualSessionData]);

  // Listen for authentication events from popup windows or other sources
  useEffect(() => {
    const handleAuthEvent = (event: MessageEvent) => {
      // Only accept messages from our own origin
      if (event.origin !== window.location.origin) {
        return;
      }

      if (
        event.data?.type === "auth-success" ||
        event.data?.type === "session-updated"
      ) {
        console.log(
          "🔄 CONVERSION PROVIDER: Auth event detected, will check session after delay",
        );

        // Small delay to ensure session is properly set
        setTimeout(() => {
          void (async () => {
            // Manually fetch the session using authClient
            try {
              const freshSession = await authClient.getSession();
              console.log("🔄 CONVERSION PROVIDER: Manually fetched session:", {
                hasSession: Boolean(freshSession.data),
                userEmail: freshSession.data?.user?.email,
              });

              if (freshSession.data) {
                // Update manual session data
                setManualSessionData(freshSession.data);
              }
            } catch (error) {
              console.error(
                "❌ CONVERSION PROVIDER: Failed to manually fetch session:",
                error,
              );
            }
          })();
        }, 500); // 500ms delay to ensure session is set

        // Force a re-render to check session state
        setAuthEventTriggered((prev) => prev + 1);
      }
    };

    // Also listen for storage events (cross-tab session updates)
    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key === "better-auth.session.token" ||
        event.key === "auth-session"
      ) {
        console.log("🔄 CONVERSION PROVIDER: Storage auth event detected");

        // Manually fetch the session
        void (async () => {
          try {
            const freshSession = await authClient.getSession();
            if (freshSession.data) {
              setManualSessionData(freshSession.data);
            }
          } catch (error) {
            console.error(
              "❌ CONVERSION PROVIDER: Failed to fetch session on storage event:",
              error,
            );
          }
        })();

        setAuthEventTriggered((prev) => prev + 1);
      }
    };

    window.addEventListener("message", handleAuthEvent);
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("message", handleAuthEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  // Debug logging for auth state changes
  useEffect(() => {
    if (isLoaded) {
      console.log("🔍 CONVERSION PROVIDER AUTH STATE:", {
        isSignedIn,
      });
    }
  }, [isLoaded, isSignedIn]);

  // Just render children - this provider works silently in the background
  return <>{children}</>;
}
